import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { Call, Device } from '@twilio/voice-sdk';

import voiceService, { CallPermissionResult } from '../services/voiceService';

/**
 * Holds the therapist's active WhatsApp call.
 *
 * Lives above the router so a call survives navigation — the same reason the video
 * call context does: the therapist keeps working through the conversation while the
 * floating widget stays put.
 *
 * Only WhatsApp calls appear here. A phone conference call is placed entirely by
 * Twilio (both legs are real phones), so there is no browser-side call to track.
 */

export type CallStatus = 'connecting' | 'ringing' | 'in-call' | 'ended' | 'error';

export interface ActiveCall {
  patientId: number;
  patientName: string;
  phone: string; // +E164, as the backend will dial it
  meetingId?: number;
  status: CallStatus;
  startedAt: number | null;
  error?: string;
}

interface StartCallOptions {
  patientId: number;
  patientName: string;
  phone?: string;
  meetingId?: number;
}

interface CallContextValue {
  activeCall: ActiveCall | null;
  isMuted: boolean;
  startWhatsAppCall: (options: StartCallOptions) => Promise<void>;
  hangUp: () => void;
  toggleMute: () => void;
  dismissCall: () => void;
  requestCallPermission: (patientId: number, phone: string) => Promise<CallPermissionResult>;
}

const CallContext = createContext<CallContextValue | null>(null);

export const useCall = (): CallContextValue => {
  const ctx = useContext(CallContext);
  if (!ctx) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return ctx;
};

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  const getDevice = useCallback(async (): Promise<Device> => {
    if (deviceRef.current) {
      return deviceRef.current;
    }
    // Loaded on demand: the Voice SDK is ~50 kB gzip and must not sit in the main
    // bundle for the therapists who never place a WhatsApp call.
    const { Device } = await import('@twilio/voice-sdk');
    const { token } = await voiceService.getVoiceToken();
    const device = new Device(token, {
      // Opus first — both legs are VoIP end to end.
      codecPreferences: ['opus', 'pcmu'] as any,
    });
    device.on('tokenWillExpire', async () => {
      try {
        const refreshed = await voiceService.getVoiceToken();
        device.updateToken(refreshed.token);
      } catch (error) {
        console.error('[Call] Voice token refresh failed', error);
      }
    });
    device.on('error', (error: any) => {
      console.error('[Call] Device error', error);
    });
    deviceRef.current = device;
    return device;
  }, []);

  const startWhatsAppCall = useCallback(
    async ({ patientId, patientName, phone, meetingId }: StartCallOptions) => {
      if (callRef.current) {
        console.warn('[Call] A call is already in progress');
        return;
      }
      setIsMuted(false);
      setActiveCall({ patientId, patientName, phone: phone || '', meetingId, status: 'connecting', startedAt: null });
      try {
        const device = await getDevice();
        const params: Record<string, string> = { patient_id: String(patientId) };
        if (meetingId) params.meeting_id = String(meetingId);
        if (phone) params.phone = phone;

        // The browser leg joins the conference immediately, so nothing here can tell
        // whether the patient answered. On any disconnect, ask the backend to cancel
        // a WhatsApp leg that is still ringing — otherwise hanging up before the
        // patient answers leaves their phone ringing.
        const cancelRingingLeg = () => {
          voiceService
            .cancelWhatsAppCall({ patient_id: patientId, phone: phone || undefined })
            .catch(() => { /* best effort */ });
        };

        const call = await device.connect({ params });
        callRef.current = call;
        call.on('ringing', () => {
          setActiveCall((prev) => (prev ? { ...prev, status: 'ringing' } : prev));
        });
        call.on('accept', () => {
          setActiveCall((prev) => (prev ? { ...prev, status: 'in-call', startedAt: Date.now() } : prev));
        });
        call.on('disconnect', () => {
          callRef.current = null;
          setIsMuted(false);
          setActiveCall((prev) => (prev ? { ...prev, status: 'ended' } : prev));
          cancelRingingLeg();
        });
        call.on('cancel', () => {
          callRef.current = null;
          setActiveCall((prev) => (prev ? { ...prev, status: 'ended' } : prev));
          cancelRingingLeg();
        });
        call.on('error', (error: any) => {
          console.error('[Call] Call error', error);
          callRef.current = null;
          setActiveCall((prev) => (prev ? { ...prev, status: 'error', error: error?.message || 'Call failed' } : prev));
        });
        call.on('mute', (muted: boolean) => setIsMuted(muted));
      } catch (error: any) {
        console.error('[Call] Failed to start WhatsApp call', error);
        callRef.current = null;
        const detail = error?.response?.data?.detail;
        setActiveCall((prev) =>
          prev
            ? { ...prev, status: 'error', error: typeof detail === 'string' ? detail : error?.message || 'Could not start the call' }
            : prev
        );
      }
    },
    [getDevice]
  );

  const hangUp = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
    } else {
      deviceRef.current?.disconnectAll();
      setActiveCall((prev) => (prev ? { ...prev, status: 'ended' } : prev));
    }
  }, []);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    call.mute(!call.isMuted());
    setIsMuted(call.isMuted());
  }, []);

  const dismissCall = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
      callRef.current = null;
    }
    setIsMuted(false);
    setActiveCall(null);
  }, []);

  const requestCallPermission = useCallback(
    (patientId: number, phone: string) =>
      // Pass the number the call will actually dial so the request goes to the same
      // phone the therapist is trying to reach.
      voiceService.requestWhatsAppCallPermission({ patient_id: patientId, phone: phone || undefined }),
    []
  );

  return (
    <CallContext.Provider
      value={{ activeCall, isMuted, startWhatsAppCall, hangUp, toggleMute, dismissCall, requestCallPermission }}
    >
      {children}
    </CallContext.Provider>
  );
};

export default CallProvider;
